import { MarketplaceCardResponseDto } from "../../types/types";
import { getCurrencySymbol } from "../../utils/opportunities/opportunityAndReadyRoster";

export interface Pill {
  label: string;
  className: string;
}

export interface MarketCardView {
  isRoster: boolean;
  title: string;
  subtitle: string;
  pill: Pill | null;
  purse: string;
  imageSrc?: string;
  initials: string;
  sport: string;
  date: string;
  level: string;
  weightClass: string;
  location: string;
  status?: string;
  isPublished: boolean;
  isAvailable: boolean;
  isExpired: boolean;
  hasEvent: boolean;
}

const GREEN: Pill["className"] = "bg-green-300";
const BLUE: Pill["className"] = "bg-blue-300";
const VIOLET: Pill["className"] = "bg-violet-300";
const ORANGE: Pill["className"] = "bg-orange-300";
const RED: Pill["className"] = "bg-red-300";
const GREY: Pill["className"] = "bg-gray-200";

// Keys are roster statuses and opportunity types / deal types (upper-cased).
const PILLS: Record<string, Pill> = {
  FIGHT_READY: { label: "Fight ready", className: GREEN },
  TOURNAMENT_READY: { label: "Tournament ready", className: GREEN },
  SHORT_NOTICE_AVAILABLE: { label: "Short notice available", className: GREEN },
  LOOKING_FOR_COACH_OR_GYM: { label: "Looking for coach / gym", className: BLUE },
  LOOKING_FOR_MANAGER: { label: "Looking for manager", className: BLUE },
  TOURNAMENT_RECRUITMENT: { label: "Tournament recruitment", className: BLUE },
  SHORT_NOTICE_REPLACEMENT: { label: "Short notice replacement", className: BLUE },
  FIGHT_SINGLE_REPLACEMENT: { label: "Short notice replacement", className: BLUE },
  REGISTER_FOR_CHAMPIONSHIP: { label: "Register for championship", className: BLUE },
  LOOKING_FOR_SPARRING_PARTNER: { label: "Looking for sparring partner", className: VIOLET },
  SPARRING_PARTNERS_NEEDED: { label: "Looking for sparring partner", className: VIOLET },
  SPARRING: { label: "Looking for sparring partner", className: VIOLET },
  HYBRID_FIGHTING_CONTRACT: { label: "Hybrid fighting contract", className: ORANGE },
  SPONSORSHIP_OPPORTUNITY: { label: "Sponsorship opportunity", className: RED },
};

/** "SEMI_PRO" -> "Semi Pro" */
export const humanize = (value?: string | null): string =>
  value
    ? value.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
    : "";

// Backend seed has the typo "AMETEUR".
export const formatLevel = (value?: string | null): string =>
  value?.toUpperCase() === "AMETEUR" ? "Amateur" : humanize(value);

const getPill = (key?: string | null): Pill | null => {
  if (!key) return null;
  const upper = key.toUpperCase();
  return PILLS[upper] ?? { label: humanize(upper), className: GREY };
};

/** Parses "YYYY-MM-DD" as a local date (new Date("YYYY-MM-DD") is UTC and can show the previous day). */
const parseDate = (value: unknown): Date | null => {
  const text = String(value ?? "");
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatCardDate = (date: Date): string =>
  date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

const isPast = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date.getTime() < today.getTime();
};

export const formatPurse = (purse: unknown, currency?: string): string => {
  if (purse === undefined || purse === null || purse === "") return "";
  const symbol = getCurrencySymbol(currency || "");
  const withSymbol = (text: string) => `${text} ${symbol}`.trim();

  if (typeof purse === "number" || !Number.isNaN(Number(purse))) {
    return withSymbol(`${Number(purse)}`);
  }
  const value = String(purse).toUpperCase();
  if (value === "NOT_DECIDED") return "Not decided";
  const range = /^(BELOW|ABOVE)_(\d+)$/.exec(value);
  if (range) return withSymbol(`${humanize(range[1])} ${range[2]}`);
  return withSymbol(humanize(value));
};

export const buildMarketCardView = (
  item: MarketplaceCardResponseDto,
): MarketCardView | null => {
  const roster = item?.readyRosterCard;
  const opportunity = item?.opportunityCardResponse;
  if (!roster && !opportunity) return null;

  const fighter = roster?.fighter;
  const manager = opportunity?.manager;
  const person = fighter ?? manager;
  const realName = `${person?.firstName ?? ""} ${person?.lastName ?? ""}`.trim();
  const nickname = fighter?.nickname?.trim();

  const dealType: string | undefined = opportunity?.templateJson?.dealType;
  const opportunityType: string | undefined = opportunity?.formData?.opportunityType;
  const details = opportunity?.formData?.details ?? {};
  const isSparring = dealType === "sparring";

  const status = roster ? roster.status : opportunity?.status ?? "AVAILABLE";
  const eventDate = roster
    ? roster.validUpto
    : details.eventDate || details.validUpto || details.startDate;
  const parsedDate = eventDate ? parseDate(eventDate) : null;

  const loc = details.location;
  const location = roster
    ? roster.location
    : (loc && typeof loc === "object" ? loc.country || loc.city || loc.venue : details.country) ?? "";

  const upperStatus = status?.toUpperCase();

  return {
    isRoster: !!roster,
    title:
      (fighter ? nickname || realName : manager?.organizationName || realName) ||
      humanize(opportunityType || dealType) ||
      "Opportunity",
    subtitle: nickname ? realName : "",
    pill: roster
      ? getPill(roster.rosterStatus)
      : getPill(
          [opportunityType, dealType].find((key) => key && PILLS[key.toUpperCase()]) ??
            opportunityType ??
            dealType,
        ),
    purse: roster
      ? formatPurse(roster.purse, roster.currency)
      : formatPurse(opportunity?.formData?.core?.baseAmount, opportunity?.formData?.core?.currency),
    imageSrc: person?.profileImage || undefined,
    initials:
      person?.firstName && person?.lastName
        ? `${person.firstName[0]}${person.lastName[0]}`.toUpperCase()
        : "OP",
    sport: roster?.sport ? humanize(roster.sport) : "",
    date: roster
      ? ""
      : parsedDate
      ? formatCardDate(parsedDate)
      : isSparring && details.durationDays
      ? `${details.durationDays} days`
      : "",
    level: isSparring && opportunity ? "" : formatLevel(roster ? roster.preferredRuleSet : details.ruleset),
    weightClass: isSparring && opportunity ? "" : humanize(roster ? roster.weightClass : details.weightClass),
    location,
    status,
    isPublished: upperStatus === "PUBLISH",
    isAvailable: opportunity?.status?.toUpperCase() === "AVAILABLE",
    isExpired: parsedDate ? isPast(parsedDate) : false,
    hasEvent: !!opportunity?.eventId,
  };
};
